import type { SuiClientTypes } from "@mysten/sui/client";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useSuiGrpcClient } from "./useSuiGrpcClient";

/**
 * MIGRATION (JSON-RPC -> gRPC):
 *
 * dapp-kit's `useSuiClientQuery("<jsonRpcMethod>", params)` cannot work over
 * gRPC — it is keyed by JSON-RPC method name. These hooks replace it with plain
 * `useQuery` over the transport-agnostic core API (`client.core.*`).
 *
 * Method mapping (JSON-RPC -> core API):
 *   getObject             -> core.getObject
 *   getOwnedObjects       -> core.listOwnedObjects
 *   getCoins              -> core.listCoins
 *   getCoinMetadata       -> core.getCoinMetadata
 *   getDynamicFields      -> core.listDynamicFields
 *   queryTransactionBlocks-> core.listTransactions
 *
 * Two response-shape differences bite every call site:
 *   - `options: { showContent: true }` becomes `include: { json: true }` (or
 *     `{ content: true }` for BCS bytes). The JSON payload is FLAT: JSON-RPC's
 *     `content.fields.price.fields.current` is now `json.price.current`.
 *   - list responses return `objects` / `transactions` / `dynamicFields` instead
 *     of `data`, and `hasNextPage` + `cursor` instead of `nextCursor`.
 */

/** Move struct contents as JSON. Shape mirrors the Move struct field names. */
export type SuiMoveJson = Record<string, unknown> | null;

const JSON_INCLUDE = { json: true } as const;

/** Replaces `useSuiClientQuery("getObject", { id, options })`. */
export function useSuiObject(
  objectId: string | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<SuiClientTypes.Object<{ json: true }> | undefined> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: ["sui", network, "getObject", objectId],
    // An empty id is a common "not resolved yet" placeholder in this codebase,
    // so treat it the same as undefined rather than firing a doomed request.
    enabled: (options?.enabled ?? true) && !!objectId,
    queryFn: async () => {
      const { object } = await client.core.getObject({
        objectId: objectId!,
        include: JSON_INCLUDE,
      });

      return object;
    },
  });
}

/** Replaces `useSuiClientQuery("getOwnedObjects", { owner, filter, cursor })`. */
export function useSuiOwnedObjects(
  input: {
    owner: string | undefined;
    /** Fully qualified or partial Move type, e.g. `0xpkg::module::License`. */
    type?: string;
    cursor?: string | null;
    limit?: number;
  },
  options?: { enabled?: boolean },
): UseQueryResult<SuiClientTypes.ListOwnedObjectsResponse<{ json: true }>> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: [
      "sui",
      network,
      "listOwnedObjects",
      input.owner,
      input.type,
      input.cursor,
      input.limit,
    ],
    enabled: (options?.enabled ?? true) && !!input.owner,
    queryFn: () =>
      client.core.listOwnedObjects({
        owner: input.owner!,
        type: input.type,
        cursor: input.cursor,
        limit: input.limit,
        include: JSON_INCLUDE,
      }),
  });
}

/** Replaces `useSuiClientQuery("getCoins", { owner, coinType })`. */
export function useSuiCoins(
  input: { owner: string | undefined; coinType?: string; limit?: number },
  options?: { enabled?: boolean },
): UseQueryResult<SuiClientTypes.ListCoinsResponse> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: ["sui", network, "listCoins", input.owner, input.coinType, input.limit],
    enabled: (options?.enabled ?? true) && !!input.owner,
    queryFn: () =>
      client.core.listCoins({
        owner: input.owner!,
        coinType: input.coinType,
        limit: input.limit,
      }),
  });
}

/** Replaces `useSuiClientQuery("getCoinMetadata", { coinType })`. */
export function useSuiCoinMetadata(
  coinType: string | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<SuiClientTypes.CoinMetadata | null> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: ["sui", network, "getCoinMetadata", coinType],
    enabled: (options?.enabled ?? true) && !!coinType,
    queryFn: async () => {
      const { coinMetadata } = await client.core.getCoinMetadata({
        coinType: coinType!,
      });

      return coinMetadata;
    },
  });
}

/** Replaces `useSuiClientQuery("getDynamicFields", { parentId })`. */
export function useSuiDynamicFields(
  input: {
    parentId: string | undefined;
    cursor?: string | null;
    limit?: number;
  },
  options?: { enabled?: boolean },
): UseQueryResult<SuiClientTypes.ListDynamicFieldsResponse> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: ["sui", network, "listDynamicFields", input.parentId, input.cursor, input.limit],
    enabled: (options?.enabled ?? true) && !!input.parentId,
    queryFn: () =>
      client.core.listDynamicFields({
        parentId: input.parentId!,
        cursor: input.cursor,
        limit: input.limit,
      }),
  });
}

/** Resolves checkpoint timestamps (ms) for a set of transaction digests. */
export function useSuiTransactionTimestamps(
  digests: string[],
  options?: { enabled?: boolean },
): UseQueryResult<Record<string, number>> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: ["sui", network, "transactionTimestamps", [...digests].sort()],
    enabled: (options?.enabled ?? true) && digests.length > 0,
    queryFn: async () => {
      // MIGRATION: the core transaction shape has no `timestampMs` — the field
      // exists on the underlying gRPC `ExecutedTransaction` but the core API never
      // requests it, so ask for it explicitly via a read mask.
      const { response } = await client.ledgerService.batchGetTransactions({
        digests,
        readMask: { paths: ["digest", "timestamp"] },
      });

      const timestamps: Record<string, number> = {};

      for (const result of response.transactions) {
        if (result.result.oneofKind !== "transaction") continue;

        const { digest, timestamp } = result.result.transaction;

        if (!digest || !timestamp) continue;

        timestamps[digest] =
          Number(timestamp.seconds) * 1000 + Math.floor((timestamp.nanos ?? 0) / 1_000_000);
      }

      return timestamps;
    },
  });
}

/**
 * Replaces `useSuiClientQuery("queryTransactionBlocks", { filter, options })`.
 *
 * The core API filter accepts EXACTLY ONE predicate — either `sender` or
 * `function` (a `::`-delimited Move path). JSON-RPC's `MatchAll` of
 * `FromAddress` + `MoveFunction` has no equivalent; filter the remaining
 * predicate client-side if you need both.
 */
export function useSuiTransactions<Include extends SuiClientTypes.TransactionInclude>(
  input: {
    filter?: SuiClientTypes.TransactionFilter;
    order?: "ascending" | "descending";
    limit?: number;
    include: Include;
  },
  options?: { enabled?: boolean },
): UseQueryResult<SuiClientTypes.ListTransactionsResponse<Include>> {
  const { client, network } = useSuiGrpcClient();

  return useQuery({
    queryKey: [
      "sui",
      network,
      "listTransactions",
      input.filter,
      input.order,
      input.limit,
      input.include,
    ],
    enabled: options?.enabled ?? true,
    queryFn: () =>
      client.core.listTransactions({
        filter: input.filter,
        order: input.order,
        limit: input.limit,
        include: input.include,
      }),
  });
}
